import {
  TaxForecastProfileFamilySituation,
  TaxForecastProfileTaxCalculationMode,
} from '__generated__/GQL';

import {
  PersonalizedTaxTypeSchema,
  StandardTaxTypeSchema,
  TaxTypeSchema,
} from '../taxType.schema';

export type StandardTaxTypeInput = {
  taxCalculationMode: TaxForecastProfileTaxCalculationMode.Standard;
  familyAdditionalRevenue: number;
  familySituation: TaxForecastProfileFamilySituation;
  isAloneWithChild: boolean;
  numberOfChildren: number;
};

/**
 * Default values are derived from URSSAF documentation
 * @link https://mon-entreprise.urssaf.fr/documentation/imp%C3%B4t
 */
const formatStandardTaxTypeInput = (
  formInput: StandardTaxTypeSchema,
): StandardTaxTypeInput => {
  const input = {
    // Must use logical OR operator here as the value can be NaN, null coalescing doesn't affect it
    familyAdditionalRevenue: formInput.additionalHouseholdRevenue || 0,
    familySituation: formInput.familySituation,
    isAloneWithChild: formInput.isSingleParent,
    numberOfChildren: formInput.numberOfChildren || 0,
    taxCalculationMode: TaxForecastProfileTaxCalculationMode.Standard,
  } satisfies StandardTaxTypeInput;

  if (
    input.numberOfChildren <= 0 ||
    input.familySituation === TaxForecastProfileFamilySituation.Couple
  ) {
    input.isAloneWithChild = false;
  }

  return input;
};

export type PersonalizedTaxTypeInput = {
  taxCalculationMode: TaxForecastProfileTaxCalculationMode.Custom;
  customTaxRate: number;
};

const formatPersonalizedTaxTypeInput = (
  formInput: PersonalizedTaxTypeSchema,
): PersonalizedTaxTypeInput => ({
  customTaxRate: formInput.customRate || 0,
  taxCalculationMode: TaxForecastProfileTaxCalculationMode.Custom,
});

export type NeutralTaxTypeInput = {
  taxCalculationMode: TaxForecastProfileTaxCalculationMode.Neutral;
};

const formatNeutralTaxTypeInput = (): NeutralTaxTypeInput => ({
  taxCalculationMode: TaxForecastProfileTaxCalculationMode.Neutral,
});

export const formatTaxTypeInput = (formInput: TaxTypeSchema) => {
  switch (formInput.taxCalculationMode) {
    case TaxForecastProfileTaxCalculationMode.Standard:
      return formatStandardTaxTypeInput(formInput);
    case TaxForecastProfileTaxCalculationMode.Custom:
      return formatPersonalizedTaxTypeInput(formInput);
    case TaxForecastProfileTaxCalculationMode.Neutral:
      return formatNeutralTaxTypeInput();
    default:
      throw new Error('Unhandled taxCalculationMode in formatTaxTypeInput');
  }
};
